import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import HealthLoopLogo from '@/components/HealthLoopLogo';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH. This page handles the post-Google-OAuth redirect
// where Emergent Auth appends `#session_id=...` to the dashboard URL.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthCallback = () => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const { t } = useLanguage();
  const [error, setError] = useState('');
  // useRef (not useState) so we set the processed flag synchronously
  // before React StrictMode's double-invoke can run this effect twice.
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    // Always strip the fragment immediately so a hard refresh can't replay
    // the same session_id, and so the URL bar shows a clean /dashboard.
    try {
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (_) { /* ignore */ }

    if (!sessionId) {
      navigate('/login', { replace: true });
      return;
    }

    const exchange = async () => {
      // Bounded so the screen can never sit on "Signing you in…" indefinitely.
      // If anything in the chain (session exchange, /auth/me hydration) is
      // slower than this, we surface a clear error + fallback to /login.
      const HARD_TIMEOUT_MS = 18000;
      const exchangeTimer = setTimeout(() => {
        // eslint-disable-next-line no-console
        console.error('[AuthCallback] hard timeout reached — falling back to /login');
        setError(t('auth.googleSlowOrFailed') || 'Sign-in is taking longer than expected. Please try again.');
        setTimeout(() => navigate('/login', { replace: true }), 1800);
      }, HARD_TIMEOUT_MS);

      try {
        let browserTz = null;
        try { browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch (_) { /* tz lookup unsupported */ }
        const headers = { 'X-Session-ID': sessionId };
        if (browserTz) headers['X-Browser-Timezone'] = browserTz;
        // eslint-disable-next-line no-console
        console.info('[AuthCallback] POST /auth/google/session — exchange start');
        const { data } = await axios.post(
          `${API}/auth/google/session`,
          {},
          { withCredentials: true, headers, timeout: 15000 },
        );
        // eslint-disable-next-line no-console
        console.info('[AuthCallback] exchange OK role=%s id=%s', data?.role, data?.id?.slice?.(0, 8));
        // Persist any returned tokens for the cross-origin Bearer fallback.
        if (data?.tokens && typeof window !== 'undefined') {
          window.__hlAuth?.storeTokens?.(data.tokens);
        }
        try { localStorage.setItem('healthloop_user', JSON.stringify(data)); } catch (_) { /* storage disabled */ }
        // Re-hydrate AuthContext from /auth/me using the freshly issued
        // cookies/tokens, then route by role.
        await refreshUser();
        try { sessionStorage.removeItem('healthloop_session_expired'); } catch (_) { /* storage disabled */ }
        clearTimeout(exchangeTimer);
        navigate(data?.role === 'admin' ? '/admin' : '/dashboard', { replace: true });
      } catch (err) {
        clearTimeout(exchangeTimer);
        const detail = err?.response?.data?.detail;
        const isTimeout = err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '');
        // eslint-disable-next-line no-console
        console.error('[AuthCallback] exchange FAILED', {
          status: err?.response?.status,
          isTimeout,
          detail,
          message: err?.message,
        });
        const fallbackMsg = isTimeout
          ? (t('auth.googleSlowOrFailed') || 'Sign-in is taking longer than expected. Please try again.')
          : (t('auth.googleFailed') || 'Google sign-in failed.');
        setError(typeof detail === 'string' ? detail : fallbackMsg);
        setTimeout(() => navigate('/login', { replace: true }), 1800);
      }
    };

    exchange();
  }, [navigate, refreshUser, t]);

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 p-6"
      data-testid="auth-callback-screen"
    >
      <div className="flex flex-col items-center gap-4 text-center">
        <HealthLoopLogo size="md" showText={false} />
        {error ? (
          <>
            <p className="text-sm text-red-600 max-w-xs" data-testid="auth-callback-error">{error}</p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="mt-1 text-xs font-medium text-emerald-700 hover:underline"
              data-testid="auth-callback-back-to-login"
            >
              Back to sign-in
            </button>
          </>
        ) : (
          <>
            <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
            <p className="text-sm text-slate-600" data-testid="auth-callback-loading">
              {t('auth.googleSigningIn') || 'Signing you in…'}
            </p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="mt-3 text-xs text-slate-400 hover:text-slate-600 hover:underline transition-colors"
              data-testid="auth-callback-cancel"
            >
              Taking too long? Return to sign-in
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
